/* eslint-disable max-lines */
import { Box } from '@chakra-ui/react';
import {
  Button,
  Group,
  LargeFileUpload,
  OnSubmissionStateChange,
  SplitScreen,
  useBreakpoint,
  useDisclosure,
  useMelioForm,
  UseMelioFormResults,
  useTheme,
  useToast,
  useWatch,
} from '@melio/penny';
import { useAnalytics, withAnalyticsContext } from '@melio/platform-analytics';
import {
  AccountingPlatformSlug,
  Bill,
  FileInfo,
  useAccountingPlatformBillLabels,
  useAccountingPlatformBillLineItemLabels,
  useAccountingPlatformCategories,
  useAccountingPlatformItems,
  useAccountingPlatforms,
  useAccountsWithLogoUrl,
  useVendors,
} from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';
import { getDollarsFromCents, useDateUtils } from '@melio/platform-utils';
import { isEmpty, keys } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { useUploadBillFile, UseUploadBillFileProps } from '../../../utils/useUploadBillFile';
import { useVendorSelectSideEffects } from '../../../utils/useVendorSelectSideEffects';
import { AmountsMismatchModal } from '../../ReviewScannedInvoice/components/AmountsMismatchModal';
import { InvoiceNumberSchema, SetInvoiceNumberModal } from '../../set-invoice-number-modal';
import { BillFileOcrData, getInitialValueLineItemWithAmount, useGetBillFileOcrData } from '../bill-utils';
import { AddBillFormPanel } from './components/LineItems/AddBillFormPanel/AddBillFormPanel';
import { NewBillHeader } from './components/NewBillHeader';
import { ReplaceExistingDetailsModal } from './components/ReplaceExistingDetailsModal';
import { useAddBillV2Schema } from './hooks/useAddBillV2Schema';
import { useDefaultFormValues } from './hooks/useDefaultFormValues';
import {
  AddBillV2DFormInitialValues,
  AddBillV2FormValues,
  AddBillV2FormValuesResult,
  AddBillV2OriginEnum,
  AddBillV2RecurringEndBy,
  AddBillV2SubmitType,
  CustomFooterProps,
  SubmitButtonProps,
} from './types';
import { calculateTotalLineItemsCount, formatFormValues, isRecurringFrequency, scrollToFirstError } from './utils';

export type AddBillV2FormActivityProps = {
  onClose: VoidFunction;
  onBack: VoidFunction;
  onDone: (values: AddBillV2FormValuesResult, submitType: AddBillV2SubmitType) => Promise<Bill | null | void> | void;
  initialValues?: AddBillV2DFormInitialValues;
  isSaving?: boolean;
  isLoading?: boolean;
  onSubmissionStateChange?: OnSubmissionStateChange<AddBillV2FormValues>;
  customFooter?: React.ElementType<CustomFooterProps>;
  allowModifyingFile?: boolean;
  initialFile?: File;
  fileInfo?: FileInfo;
  origin?: AddBillV2OriginEnum;
  isAdvancedView?: boolean;
};

export const AddBillV2FormActivity = withAnalyticsContext<AddBillV2FormActivityProps>(
  ({ setAnalyticsProperties, origin = AddBillV2OriginEnum.New, ...props }) => {
    const {
      hasAccountingPlatform,
      activeAccountingPlatform,
      isLoading: isAccountingPlatformsLoading,
    } = useAccountingPlatforms();
    const { isFetching: isLoadingAccounts } = useAccountsWithLogoUrl({ enabled: false });
    const { track } = useAnalytics();
    const toast = useToast();
    const theme = useTheme();
    const {
      settings: {
        newBillExperience: { isRecurringEnabled },
      },
    } = useConfig();
    const [savedSubmitType, setSavedSubmitType] = useState<string | undefined>();
    const [isGetBillFileOcrDataOnBillDetailsScreenEnabled] = useFeature(
      FeatureFlags.GetBillFileOcrDataOnBillDetailsScreen,
      false
    );
    const [isPlatformBillClassesQboEnabled] = useFeature(FeatureFlags.PlatformBillClassesQbo, false);
    const [isPlatformBillLocationsEnabled] = useFeature(FeatureFlags.PlatformBillLocations, false);
    const isMinifiedEligible = origin === AddBillV2OriginEnum.New && !props.initialFile && !props.isAdvancedView;
    const [isMinifiedView, setIsMinifiedView] = useState<boolean>(isMinifiedEligible);
    const [shouldClearVendorIdField, setShouldClearVendorFieldId] = useState<boolean>(false);
    const [isReplaceExistingDetailsModalOpen, setIsReplaceExistingDetailsModalOpen] = useState(false);
    const { isExtraSmallScreen: isMobile } = useBreakpoint();
    const [isAmountMismatchModalOpen, setIsAmountMismatchModalOpen] = useState(false);
    const {
      isOpen: isShowAddInvoiceNumberModal,
      onOpen: onShowAddInvoiceNumberModal,
      onClose: onCloseAddInvoiceNumberModal,
    } = useDisclosure();
    const isQuickBooksOnline = activeAccountingPlatform?.accountingSlug === AccountingPlatformSlug.QuickBooksOnline;
    const { formatMessage, formatCurrency } = useMelioIntl();

    const { isLoading: isItemsLoading, hasAccountingPlatformItems } = useAccountingPlatformItems({
      accountingPlatformId: activeAccountingPlatform?.id,
      enabled: hasAccountingPlatform,
    });

    const { isLoading: isLoadingCategories } = useAccountingPlatformCategories({
      accountingPlatformId: activeAccountingPlatform?.id,
      enabled: hasAccountingPlatform,
    });

    const { data: billLineItemLabels, isLoading: isLoadingBillLineItemLabels } =
      useAccountingPlatformBillLineItemLabels({
        accountingPlatformId: activeAccountingPlatform?.id,
        enabled: isPlatformBillClassesQboEnabled && isQuickBooksOnline,
      });

    const { data: billLabels, isLoading: isLoadingBillLabels } = useAccountingPlatformBillLabels({
      accountingPlatformId: activeAccountingPlatform?.id,
      enabled: isPlatformBillLocationsEnabled && isQuickBooksOnline,
    });

    const { isLoading: isLoadingVendors, data: vendors } = useVendors();

    const handleUploadFileError = () => {
      track('Bill', 'Status', {
        Intent: 'invoice-upload-ended',
        Status: 'Failure',
      });
    };

    const handleUploadFileStarted = () => {
      track('Bill', 'Status', {
        Intent: 'invoice-upload-started',
        Status: 'Success',
      });
    };

    const handleUploadFileFinished: UseUploadBillFileProps['onUploadFinished'] = async (newFileInfo) => {
      track('Bill', 'Status', {
        Intent: 'invoice-upload-ended',
        Status: 'Success',
      });

      if (isGetBillFileOcrDataOnBillDetailsScreenEnabled && newFileInfo && newFileInfo?.id !== props.fileInfo?.id) {
        const areInitialValuesExists =
          !isEmpty(props.initialValues) && Object.values(props.initialValues).some((v) => !!v);
        const billFileOcrDataResult = await getBillFileOcrData(newFileInfo?.id);
        if (billFileOcrDataResult) {
          setIsMinifiedView(false);

          if (
            melioFormProps.formState.isDirty ||
            !isEmpty(melioFormProps.formState.dirtyFields) ||
            areInitialValuesExists
          ) {
            setIsReplaceExistingDetailsModalOpen(true);
          } else {
            handleReplace(billFileOcrDataResult);
          }
        }
      }
    };

    const {
      file,
      fileValue,
      setFile,
      fileInfo,
      isLoading: isFileLoading,
    } = useUploadBillFile({
      initialFileInfo: props.fileInfo,
      initialFile: props.initialFile,
      onError: handleUploadFileError,
      onUploadStarted: handleUploadFileStarted,
      onUploadFinished: handleUploadFileFinished,
    });

    const { defaultFormValues, getDefaultFormValues } = useDefaultFormValues({
      values: props.initialValues,
      hasAccountingPlatform,
      hasAccountingPlatformItems,
      activeAccountingPlatform,
    });
    const schema = useAddBillV2Schema({
      initialValues: { amount: props.initialValues?.amount, balance: props.initialValues?.balance },
      isMinifiedView,
    });
    const { createDate } = useDateUtils();
    const melioFormProps = useMelioForm<AddBillV2FormValues>({
      onSubmit: async (values, _, submitType = AddBillV2SubmitType.SAVE_AND_CLOSE) => {
        const { lineItems, ...restValues } = formatFormValues({
          values,
          createDate,
          isAdvancedBillFlow: !isMinifiedView,
          billLineItemLabels,
          activeAccountingPlatform,
        });
        restValues.invoiceDate = restValues?.invoiceDate || createDate();
        if (props?.initialValues?.balance && values?.amount) {
          const currentBalance = props?.initialValues?.balance;
          const currentAmount = parseFloat(values?.amount);
          const previousAmount = parseFloat(props?.initialValues?.amount || '0');
          restValues.balance = currentBalance + currentAmount - previousAmount;
        }
        const lineItemsCount = calculateTotalLineItemsCount({ isMinifiedView, values });

        const analyticsProps = {
          Cta: submitType === AddBillV2SubmitType.CONTINUE_TO_PAY ? 'continue-to-pay' : 'save-and-close',
          amount: values?.amount,
          PaymentFrequency: values?.frequency,
          RecurringStartDate: restValues?.recurringStartDate,
          RecurringPaymentEndBy: restValues?.recurringEndBy,
          AccountingSoftwareType: activeAccountingPlatform?.accountingSlug,
          RecurringPaymentEndValue:
            restValues?.recurringEndBy === AddBillV2RecurringEndBy.DATE
              ? restValues?.recurringEndDate?.toISOString()
              : restValues?.recurringOccurrences,
          LineItemsCount: lineItemsCount,
          IsRecurring: isRecurringFrequency(values.frequency),
          RecurringEndDate: restValues?.recurringEndDate,
          LastPaymentAmount: values.lastAmount,
          NumberOfPayments: values.recurringOccurrences,
        };

        track('Bill', 'Click', analyticsProps);
        const newBill = await props.onDone(
          {
            ...restValues,
            lineItems,
            fileId: fileInfo?.id,
            file: file ?? undefined,
          },
          submitType as AddBillV2SubmitType
        );

        if (newBill) {
          track('Bill', 'Status', {
            ...analyticsProps,
            PageName: 'bill-details',
            BillId: newBill.id,
            Status: 'success',
            Location: !!newBill?.externalLabelId,
            LineItemsDetails: lineItems.map((lineItem) => ({
              item: !!lineItem.externalItemId,
              class: lineItem.labels?.some((label) => !isEmpty(label.externalLabelId)),
              category: !!lineItem.externalCategoryId,
              description: !!lineItem.description,
            })),
          });
          toast({
            type: 'success',
            title: formatMessage('activities.addBillV2.billForm.success.toast', {
              amount: formatCurrency(getDollarsFromCents(newBill.amount)),
              vendorName: newBill?.vendor?.name || '',
            }),
          });
        }
      },
      isSaving: props.isSaving,
      subscribeToDefaultValuesChanges: true,
      defaultValues: defaultFormValues,
      onSubmissionStateChange: props.onSubmissionStateChange,
      schema,
    });
    const { submitButtonProps, handleSubmit, getValues } = melioFormProps;
    const { isSuvcOverStpVendor, isFetching: isFetchingSelectedVendor } = useVendorSelectSideEffects(
      getValues('vendorId')
    );

    const frequency = useWatch({
      control: melioFormProps.control,
      name: 'frequency',
    });
    const shouldHideSecondaryButton = isRecurringFrequency(frequency);

    useEffect(() => {
      setAnalyticsProperties({
        PageName: 'bill-details',
        Flow: isMinifiedView ? 'simplified' : 'advanced',
        Intent: 'add-bill',
        IsSynced: !!hasAccountingPlatform,
        LineItems: isMinifiedView ? false : true,
        BillId: props.initialValues?.id?.toString(),
        amount: props.initialValues?.amount?.toString(),
        FileAttached: !!file,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMinifiedView]);

    useEffect(() => {
      const lineItemsCount = calculateTotalLineItemsCount({ isMinifiedView, values: melioFormProps?.getValues() });

      track('Bill', 'View', {
        LineItemsCount: lineItemsCount,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const vendorName = useMemo(
      () => vendors?.find(({ id }) => melioFormProps.getValues('vendorId') === id)?.name as string,
      [vendors, melioFormProps]
    );

    useEffect(() => {
      if (shouldClearVendorIdField) {
        setShouldClearVendorFieldId(false);
      }
    }, [shouldClearVendorIdField]);

    useEffect(() => {
      const formErrors = melioFormProps?.formState?.errors;
      if (!isEmpty(formErrors)) {
        const hasValidationErrorFields = scrollToFirstError('input[aria-invalid=true]');
        const hiddenValidationFields = !hasValidationErrorFields ? formErrors : undefined;

        const errorType = keys(formErrors)?.[0];
        if (errorType) {
          track('Bill', 'Status', {
            Status: 'failure',
            ErrorType: `${errorType}-error`,
            HiddenValidationField: hiddenValidationFields,
          });
        }
      }
    }, [melioFormProps?.formState?.errors, track]);

    const {
      isLoading: isFileOCRDataLoading,
      getBillFileOcrData,
      billFileOcrData,
    } = useGetBillFileOcrData({
      billFileInfo: fileInfo,
      isLineItemsEnabled: true,
      defaultBillDetails: props.initialValues,
    });

    const isLoading =
      isAccountingPlatformsLoading ||
      isItemsLoading ||
      isLoadingCategories ||
      isLoadingVendors ||
      isLoadingBillLineItemLabels ||
      isLoadingBillLabels ||
      isLoadingAccounts ||
      props.isLoading;

    const setFormValues = (values?: AddBillV2DFormInitialValues) => {
      // Because of the implementation of VendorSelect widget selected vendor value is not updated
      // when initial value is already set and form state is updated programmatically.
      // Because of it we need explicitly clear VendorSelect select and only after set the value.
      // setTimeout is needed to make sure that we'll set form state when vendor value will be cleared
      setShouldClearVendorFieldId(true);
      setTimeout(() => {
        melioFormProps.reset(values, { keepDefaultValues: true });
      });
    };

    const handleSubmitBillForm: UseMelioFormResults<AddBillV2FormValues>['handleSubmit'] =
      (target?: string) => async () => {
        const isFormValid = await melioFormProps.trigger();
        const shouldAskPayorToFillInvoiceNumber =
          isSuvcOverStpVendor && isEmpty(melioFormProps.getValues('invoiceNumber')) && isFormValid;

        if (shouldAskPayorToFillInvoiceNumber) {
          onShowAddInvoiceNumberModal();
          setSavedSubmitType(target);
        } else {
          handleSubmit(target)();
        }
      };

    const handleReplace = (billFileOcrDataResult: BillFileOcrData) => {
      setIsReplaceExistingDetailsModalOpen(false);

      if (billFileOcrDataResult?.mismatchedAmount) {
        setIsAmountMismatchModalOpen(true);
      } else {
        setFormValues(getDefaultFormValues(billFileOcrDataResult?.values));
      }
    };

    const handleCancel = () => {
      setIsReplaceExistingDetailsModalOpen(false);
    };

    const handleAutoAddLines = () => {
      if (!billFileOcrData) {
        return;
      }
      setShouldClearVendorFieldId(true);
      const values = { ...billFileOcrData.values } as AddBillV2DFormInitialValues;
      values.lineItems = [
        ...(values.lineItems ?? []),
        getInitialValueLineItemWithAmount(
          billFileOcrData?.mismatchedAmount,
          formatMessage('widgets.ReviewScannedInvoice.amountsMismatch.lineItem.desc')
        ),
      ];

      setFormValues(getDefaultFormValues(values));
      setIsAmountMismatchModalOpen(false);
    };

    const handleRemoveLines = () => {
      if (!billFileOcrData) {
        return;
      }
      const values = { ...billFileOcrData.values } as AddBillV2DFormInitialValues;
      values.lineItems = [
        getInitialValueLineItemWithAmount(
          Number(values.amount ?? '') ?? 0,
          formatMessage('widgets.ReviewScannedInvoice.amountsMismatch.lineItem.desc')
        ),
      ];

      setFormValues(getDefaultFormValues(values));
      setIsAmountMismatchModalOpen(false);
    };

    const handleUpdateInvoiceNumber = ({ invoiceNumber }: InvoiceNumberSchema) => {
      melioFormProps.setValue('invoiceNumber', invoiceNumber);
      onCloseAddInvoiceNumberModal();
      handleSubmit(savedSubmitType)();
    };

    const handleSkipAddingInvoiceNumber = () => {
      onCloseAddInvoiceNumberModal();
      handleSubmit(savedSubmitType)();
    };

    const allowRecurring = isRecurringEnabled && !props.initialValues?.id && !isMinifiedView;
    return (
      <>
        <SplitScreen
          isLoading={isLoading}
          shouldAllowResizing
          header={<NewBillHeader onClose={props.onClose} />}
          panelA={{
            maxWidth: 34,
            padding: { xs: 'xxl', m: 'xl' },
            content: (
              <>
                <AmountsMismatchModal
                  mismatchedValue={`$${billFileOcrData?.mismatchedAmount ?? 0}`}
                  isOpen={isAmountMismatchModalOpen}
                  onAdd={handleAutoAddLines}
                  onRemove={handleRemoveLines}
                />
                <ReplaceExistingDetailsModal
                  isOpen={isReplaceExistingDetailsModalOpen}
                  onClose={handleCancel}
                  onReplace={() => handleReplace(billFileOcrData)}
                  onCancel={handleCancel}
                />
                <LargeFileUpload
                  value={fileValue}
                  onChange={setFile}
                  acceptTypes={['pdf', 'png', 'jpg']}
                  isLoading={isFileLoading || isFileOCRDataLoading}
                  isReadOnly={props.allowModifyingFile}
                  data-testid="add-bill-v2-file-upload"
                />
              </>
            ),
          }}
          panelB={{
            padding: 'none',
            content: (
              <Box
                background={theme.colors.neutral.white}
                pt={{ xs: 'xl', m: 'xxl' }}
                px={{ xs: 's', m: 'xxl' }}
                minH="100%"
              >
                <AddBillFormPanel
                  melioFormProps={melioFormProps}
                  activeAccountingPlatform={activeAccountingPlatform}
                  shouldClearVendorIdField={shouldClearVendorIdField}
                  isConnectedToAccountingPlatform={hasAccountingPlatform}
                  hasInvoice={!!(file || fileInfo)}
                  allowRecurring={allowRecurring}
                  isMinifiedView={isMinifiedView}
                  setIsMinifiedView={setIsMinifiedView}
                  billLineItemLabels={billLineItemLabels}
                  billLabel={billLabels?.[0]}
                />
              </Box>
            ),
            footer: (
              <Box
                justifyContent="flex-end"
                width="full"
                py="s"
                paddingX="m"
                background={theme.colors.neutral.white}
                borderTop={`1px solid ${theme.colors.neutral.light}`}
              >
                <Group variant="vertical" alignItems="flex-end">
                  {props.customFooter ? (
                    <Group>
                      <props.customFooter onSubmitForm={handleSubmitBillForm} />
                    </Group>
                  ) : (
                    <>
                      <Group width={isMobile ? 'full' : undefined}>
                        <AddBillV2FormButtons
                          handleSubmit={handleSubmitBillForm}
                          submitButtonProps={{
                            ...submitButtonProps,
                            isDisabled: isFetchingSelectedVendor || submitButtonProps.isDisabled,
                          }}
                          shouldHideSecondaryButton={shouldHideSecondaryButton}
                        />
                      </Group>
                    </>
                  )}
                </Group>
              </Box>
            ),
          }}
        />
        {vendors?.length ? (
          <SetInvoiceNumberModal
            analytics={{
              contextView: 'Bill',
            }}
            onSubmitInvoiceNumber={handleUpdateInvoiceNumber}
            isOpen={isShowAddInvoiceNumberModal}
            onClose={handleSkipAddingInvoiceNumber}
            onContinueWithoutInvoiceNumber={handleSkipAddingInvoiceNumber}
            vendorName={vendorName}
          />
        ) : null}
      </>
    );
  }
);

type AddBillV2FormButtonsProps = {
  submitButtonProps: SubmitButtonProps;
  handleSubmit: UseMelioFormResults<AddBillV2FormValues>['handleSubmit'];
  shouldHideSecondaryButton?: boolean;
};

const AddBillV2FormButtons = ({
  shouldHideSecondaryButton,
  submitButtonProps,
  handleSubmit,
}: AddBillV2FormButtonsProps) => {
  const { formatMessage } = useMelioIntl();

  if (shouldHideSecondaryButton) {
    return (
      <Button
        {...submitButtonProps}
        data-testid="add-bill-v2-primary-button"
        isFullWidth
        label={formatMessage('activities.addBillV2.footer.buttons.saveAndPay')}
        onClick={handleSubmit(AddBillV2SubmitType.CONTINUE_TO_PAY)}
      />
    );
  }

  return (
    <>
      <Button
        {...submitButtonProps}
        variant="secondary"
        label={formatMessage('activities.addBillV2.footer.buttons.saveAndClose')}
        onClick={handleSubmit(AddBillV2SubmitType.SAVE_AND_CLOSE)}
        isFullWidth
        data-testid="add-bill-v2-secondary-button"
      />
      <Button
        {...submitButtonProps}
        data-testid="add-bill-v2-primary-button"
        isFullWidth
        label={formatMessage('activities.addBillV2.footer.buttons.saveAndPay')}
        onClick={handleSubmit(AddBillV2SubmitType.CONTINUE_TO_PAY)}
      />
    </>
  );
};
